

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "@/hooks/useSearchParams";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import {
  handleMfaVerify,
  handleMfaSendEmailCode,
  cancelMfaFlow,
} from "@/lib/api/handlers/AuthenticationHandling";
import { useAuth } from "@/lib/auth/AuthContext";
import { hydrateAccountPresentation } from "@/lib/auth/hydrateAccountPresentation";
import { isSwitchAccountIntent, safeNext, withNext } from "@/lib/auth/safeNext";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";

const MAX_ATTEMPTS = 5;

interface MfaVerifyState {
  otp: string[];
  recoveryCode: string;
  mode: "totp" | "recovery" | "email";
  error: string;
  isLoading: boolean;
  isEmailSending: boolean;
  emailSent: boolean;
  activeInput: number;
  timeRemaining: number;
  sessionWarning: boolean;
  attempts: number;
  emailCooldown: number;
}

export function useMfaVerifyForm(initialTimeRemaining: number) {
  const [state, setState] = useState<MfaVerifyState>({
    otp: Array(6).fill(""),
    recoveryCode: "",
    mode: "totp",
    error: "",
    isLoading: false,
    isEmailSending: false,
    emailSent: false,
    activeInput: 0,
    timeRemaining: initialTimeRemaining,
    sessionWarning: initialTimeRemaining <= 60 && initialTimeRemaining > 0,
    attempts: 0,
    emailCooldown: 0,
  });

  const navigate = useNavigate();
  const { markAuthenticated } = useAuth();
  // The login deep link, handed over by URL (billing-comms Teil 2a) and
  // validated again here — this page is public, its URL is anybody's to craft.
  const searchParams = useSearchParams();
  const next = safeNext(searchParams.get("next"));
  // "Sign in with another account" (billing-comms fix r1, X1): the login form
  // hands the exact marker on; it lifts the account-switch rule, never safeNext.
  const switching = isSwitchAccountIntent(searchParams);
  const t = useTranslations("mfaVerify");
  const tErrors = useTranslations("errors");
  const cancellingRef = useRef(false);
  // Set the moment a verify succeeds, before the awaited hydration.
  const verifiedRef = useRef(false);

  const updateState = (newState: Partial<MfaVerifyState>) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  // Session countdown timer
  useEffect(() => {
    if (state.timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setState((prev) => {
        const next = prev.timeRemaining - 1;
        if (next <= 0) {
          return { ...prev, timeRemaining: 0, sessionWarning: false, error: t("sessionExpired") };
        }
        if (next <= 60 && !prev.sessionWarning) {
          return { ...prev, timeRemaining: next, sessionWarning: true };
        }
        return { ...prev, timeRemaining: next };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [state.timeRemaining > 0]);

  // Auto-cancel when timer hits 0.
  //
  // `verifiedRef` closes a race that the awaited account hydration widened by
  // up to 1.5s: a verify that SUCCEEDS just as the window expires would
  // otherwise have its session torn down by handleCancel() two seconds later,
  // while the pending continuation still calls markAuthenticated + navigate —
  // leaving the user "authenticated" against a cancelled session, bounced back
  // to login with no explanation. mfa-setup already guards this with its
  // `isAuthenticated` flag; this lane had nothing.
  useEffect(() => {
    if (verifiedRef.current) return;
    if (state.timeRemaining === 0 && !cancellingRef.current) {
      cancellingRef.current = true;
      const timeout = setTimeout(() => handleCancel(), 2000);
      return () => clearTimeout(timeout);
    }
  }, [state.timeRemaining]);

  // Email cooldown timer
  useEffect(() => {
    if (state.emailCooldown <= 0) return;

    const interval = setInterval(() => {
      setState((prev) => {
        if (prev.emailCooldown <= 1) return { ...prev, emailCooldown: 0 };
        return { ...prev, emailCooldown: prev.emailCooldown - 1 };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [state.emailCooldown > 0]);

  useEffect(() => {
    const inputElement = document.getElementById(
      `otp-input-${state.activeInput}`,
    );
    if (inputElement) {
      (inputElement as HTMLInputElement).focus();
    }
  }, [state.activeInput]);

  function handleChange(value: string, index: number) {
    const newOtp = [...state.otp];
    newOtp[index] = value.slice(-1);

    const updates: Partial<MfaVerifyState> = { otp: newOtp, error: "" };

    if (value && index < 5) {
      updates.activeInput = index + 1;
    }

    updateState(updates);
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Backspace" && !state.otp[index] && index > 0) {
      updateState({ activeInput: index - 1 });
    }
  };

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const pastedData = event.clipboardData
      .getData("text")
      .replaceAll(" ", "")
      .slice(0, 6);

    const newOtp = [...state.otp];
    pastedData.split("").forEach((char, i) => {
      newOtp[i] = char;
    });

    const lastIndex = Math.min(pastedData.length - 1, 5);
    updateState({
      otp: newOtp,
      error: "",
      activeInput: lastIndex >= 0 ? lastIndex : 0,
    });
  }

  async function handleCancel() {
    window.dispatchEvent(new Event("navigationStart"));
    try {
      await cancelMfaFlow();
    } catch {
      // Cookie cleanup failed — navigate anyway
    }
    // Back to the login with the same deep link — a second attempt still lands there.
    navigate(withNext("/auth/login", next, { switchAccount: switching }));
  }

  function handleModeSwitch(mode: "totp" | "recovery" | "email") {
    updateState({
      mode,
      error: "",
      otp: Array(6).fill(""),
      recoveryCode: "",
      activeInput: 0,
    });
  }

  async function handleSendEmail() {
    if (state.emailCooldown > 0) return;
    updateState({ isEmailSending: true });
    try {
      const response = await handleMfaSendEmailCode();

      if (response && "isServerError" in response) {
        throwServerError(response);
      }

      updateState({ emailSent: true, isEmailSending: false, emailCooldown: 99 });
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      if (errorMessage === "mfaSessionExpired") {
        updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isEmailSending: false });
        setTimeout(() => handleCancel(), 2000);
        return;
      }

      updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isEmailSending: false });
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    // Block if too many attempts
    if (state.attempts >= MAX_ATTEMPTS) return;

    let code: string;
    const type: string = state.mode;

    if (state.mode === "totp" || state.mode === "email") {
      if (state.otp.some((digit) => digit === "")) {
        updateState({ error: t("otpIncomplete") });
        return;
      }
      code = state.otp.join("");
    } else {
      if (state.recoveryCode.length !== 8) {
        updateState({ error: t("otpIncomplete") });
        return;
      }
      code = state.recoveryCode;
    }

    updateState({ error: "", isLoading: true });

    try {
      const response = await handleMfaVerify(
        code,
        type,
      );

      if (response && "isServerError" in response) {
        throwServerError(response);
      }

      // The session is verified from here on: no expiry timer may cancel it
      // while the hydration below is still in flight.
      verifiedRef.current = true;

      // Flip AuthContext state before navigating — keeps tokenStore
      // and state in lockstep.
      // Hydrate BEFORE the auth state flips — same ordering rule as the login
      // form (see hydrateAccountPresentation).
      const hydrated = await hydrateAccountPresentation({ userId: response.userId });
      markAuthenticated(response.companyId, response.userId);
      window.dispatchEvent(new Event("navigationStart"));
      // A different account than the browser's last one: `next` was written
      // for that one — ignore it, unless the switch was asked for.
      navigate(hydrated?.accountSwitched === true && !switching ? "/" : (next ?? "/"));
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      const newAttempts = state.attempts + 1;

      if (errorMessage === "mfaSessionExpired") {
        updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isLoading: false });
        setTimeout(() => handleCancel(), 2000);
        return;
      }

      if (newAttempts >= MAX_ATTEMPTS) {
        updateState({
          error: t("tooManyAttempts"),
          isLoading: false,
          attempts: newAttempts,
        });
        setTimeout(() => handleCancel(), 3000);
        return;
      }

      updateState({
        error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"),
        isLoading: false,
        attempts: newAttempts,
      });
    }
  }

  return {
    otp: state.otp,
    recoveryCode: state.recoveryCode,
    mode: state.mode,
    error: state.error,
    isLoading: state.isLoading,
    isEmailSending: state.isEmailSending,
    emailSent: state.emailSent,
    activeInput: state.activeInput,
    timeRemaining: state.timeRemaining,
    sessionWarning: state.sessionWarning,
    attempts: state.attempts,
    emailCooldown: state.emailCooldown,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleModeSwitch,
    handleSendEmail,
    handleSubmit,
    handleCancel,
    updateState,
  };
}
